// SCR-AUTH-002 — پذیرش دعوت و تعیین رمز مالک (US-IAM-012، US-IAM-010): نام مرکز و موبایل ماسک‌شده؛ دو مسیر «حساب دارم»
// (ورود Keycloak و پذیرش با توکن — AC-IAM-012.2) و «حساب ندارم» (OTP + تعیین رمز — AC-IAM-012.1)؛ لینک منقضی با پیام
// درخواست ارسال مجدد از مدیر (AC-IAM-012.3). مسیر staff: /invitations/:token (لینک پیامک) و /invite/:token.
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type AcceptInvitationResult,
  type InvitationInfo,
  type OtpChallenge,
} from '@phoenix/api-client';
import { useAuth, usePublicApi } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, JalaliDate, KeyValueList, LoadingState } from '@phoenix/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { OtpPasswordForm } from './OtpPasswordForm';
import { PublicLayout } from './PublicLayout';

export const SCR_AUTH_002 = 'SCR-AUTH-002';

type Path = 'sign-in' | 'new-account';

function Accepted({ result, onLogin }: { result: AcceptInvitationResult; onLogin: () => void }) {
  const { t } = useTranslation('iam');
  return (
    <Stack spacing={2} data-testid="invitation-accepted" data-tenant-id={result.tenantId}>
      <Alert severity="success">
        <AlertTitle>{t('iam:invitation.accepted_title')}</AlertTitle>
        {t('iam:invitation.accepted_description')}
      </Alert>
      <Button variant="contained" onClick={onLogin} data-testid="invitation-continue">
        {t('iam:invitation.continue')}
      </Button>
    </Stack>
  );
}

/** «حساب ندارم»: ارسال OTP به موبایل دعوت‌شده و تعیین رمز (AC-IAM-012.1، AC-IAM-010.1). */
function NewAccountPath({
  token,
  info,
  onAccepted,
}: {
  token: string;
  info: InvitationInfo;
  onAccepted: (result: AcceptInvitationResult) => void;
}) {
  const { t } = useTranslation('iam');
  const api = usePublicApi();
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);

  const sendOtp = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/api/v1/identity/invitations/{token}:send-otp', { params: { path: { token } } })),
    onSuccess: setChallenge,
  });
  const accept = useMutation({
    mutationFn: ({ code, password }: { code: string; password: string }) =>
      unwrap(
        api.POST('/api/v1/identity/invitations/{token}:accept', {
          params: { path: { token } },
          body: { otpId: challenge?.otpId ?? null, code, password },
        }),
      ),
    onSuccess: onAccepted,
  });

  if (!challenge) {
    return (
      <Stack spacing={2}>
        <Typography>{t('iam:invitation.otp_intro', { mobile: faDigits(info.maskedMobile) })}</Typography>
        {sendOtp.error ? <ErrorState error={sendOtp.error} compact /> : null}
        <Box>
          <Button
            variant="contained"
            onClick={() => sendOtp.mutate()}
            disabled={sendOtp.isPending}
            data-testid="invitation-send-otp"
          >
            {t('iam:otp.send')}
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Alert severity="info" data-testid="otp-sent">
        {t('iam:otp.sent', { mobile: faDigits(info.maskedMobile) })}
      </Alert>
      <OtpPasswordForm
        submitKey={info.kind === 'OwnerSetup' ? 'iam:invitation.owner_submit' : 'iam:invitation.submit'}
        busy={accept.isPending}
        error={accept.error ?? sendOtp.error}
        resendBusy={sendOtp.isPending}
        onResend={() => {
          accept.reset();
          sendOtp.mutate();
        }}
        onSubmit={(values) => accept.mutate(values)}
      />
    </Stack>
  );
}

/** «حساب دارم»: ورود Keycloak (بازگشت به همین صفحه) و پذیرش با توکن (AC-IAM-012.2). */
function SignInPath({
  token,
  onAccepted,
}: {
  token: string;
  onAccepted: (result: AcceptInvitationResult) => void;
}) {
  const { t } = useTranslation('iam');
  const auth = useAuth();
  const api = usePublicApi();
  const accept = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/identity/invitations/{token}:accept', {
          params: { path: { token } },
          body: { otpId: null, code: null, password: null },
        }),
      ),
    onSuccess: onAccepted,
  });

  if (auth.status !== 'authenticated') {
    return (
      <Stack spacing={2}>
        <Typography>{t('iam:invitation.sign_in_intro')}</Typography>
        <Box>
          <Button
            variant="contained"
            onClick={() => void auth.login(`/invitations/${token}`)}
            data-testid="invitation-sign-in"
          >
            {t('iam:invitation.sign_in')}
          </Button>
        </Box>
      </Stack>
    );
  }

  const otherUser = isApiError(accept.error) && accept.error.code === 'PHX-IAM-0206';
  return (
    <Stack spacing={2}>
      <Typography>
        {t('iam:invitation.signed_in_as', { name: auth.user?.name ?? auth.user?.username ?? '' })}
      </Typography>
      {accept.error ? <ErrorState error={accept.error} compact /> : null}
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          onClick={() => accept.mutate()}
          disabled={accept.isPending}
          data-testid="invitation-accept"
        >
          {t('iam:invitation.accept')}
        </Button>
        {otherUser && (
          <Button onClick={() => void auth.logout()} data-testid="invitation-switch-user">
            {t('iam:invitation.switch_user')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

export function InvitationPage() {
  const { t } = useTranslation('iam');
  const { token = '' } = useParams();
  const auth = useAuth();
  const api = usePublicApi();
  const [accepted, setAccepted] = useState<AcceptInvitationResult | null>(null);
  const [path, setPath] = useState<Path | null>(null);

  const invitation = useQuery({
    queryKey: ['identity', 'public', 'invitation', token],
    queryFn: () => unwrap(api.GET('/api/v1/identity/invitations/{token}', { params: { path: { token } } })),
    retry: false,
    staleTime: Infinity,
  });
  const info = invitation.data;
  const owner = info?.kind === 'OwnerSetup';

  let body;
  if (accepted) {
    body = <Accepted result={accepted} onLogin={() => void auth.login('/')} />;
  } else if (invitation.isPending) {
    body = <LoadingState rows={3} />;
  } else if (invitation.error || !info) {
    const notFound = isApiError(invitation.error) && invitation.error.status === 404;
    body = notFound ? (
      <Alert severity="error" data-testid="invitation-invalid">
        <AlertTitle>{t('iam:invitation.invalid_title')}</AlertTitle>
        {t('iam:invitation.invalid_description')}
      </Alert>
    ) : (
      <ErrorState error={invitation.error} onRetry={() => void invitation.refetch()} />
    );
  } else if (info.state === 'Expired') {
    // AC-IAM-012.3: لینک منقضی ← درخواست ارسال مجدد از مدیر مرکز.
    body = (
      <Alert severity="warning" data-testid="invitation-expired">
        <AlertTitle>{t('iam:invitation.expired_title')}</AlertTitle>
        {t(owner ? 'iam:invitation.expired_owner' : 'iam:invitation.expired_description', {
          tenant: info.tenantName ?? '',
        })}
      </Alert>
    );
  } else if (info.state === 'Unavailable') {
    body = (
      <Stack spacing={2}>
        <Alert severity="info" data-testid="invitation-unavailable">
          <AlertTitle>{t('iam:invitation.unavailable_title')}</AlertTitle>
          {t('iam:invitation.unavailable_description')}
        </Alert>
        <Box>
          <Button variant="contained" onClick={() => void auth.login('/')}>
            {t('iam:invitation.go_to_login')}
          </Button>
        </Box>
      </Stack>
    );
  } else {
    // مسیر پیش‌فرض از سرور: حساب دارای رمز ← ورود؛ وگرنه OTP و تعیین رمز. کاربر واردشده همیشه با توکن می‌پذیرد.
    const current: Path =
      path ?? (info.requiresSignIn || auth.status === 'authenticated' ? 'sign-in' : 'new-account');
    body = (
      <Stack spacing={2}>
        <KeyValueList
          columns={2}
          items={[
            { labelKey: 'iam:invitation.tenant', value: info.tenantName, testId: 'invitation-tenant' },
            {
              labelKey: 'iam:invitation.mobile',
              value: <bdi dir="ltr">{faDigits(info.maskedMobile)}</bdi>,
              testId: 'invitation-mobile',
            },
            { labelKey: 'iam:invitation.expires', value: <JalaliDate value={info.expiresAt} withTime /> },
          ]}
        />
        <Tabs
          value={current}
          onChange={(_event, value: Path) => setPath(value)}
          aria-label={t('iam:invitation.paths_label')}
          variant="fullWidth"
        >
          <Tab value="new-account" label={t('iam:invitation.path_new')} data-testid="invitation-path-new" />
          <Tab
            value="sign-in"
            label={t('iam:invitation.path_existing')}
            data-testid="invitation-path-existing"
          />
        </Tabs>
        <Box role="tabpanel" data-path={current}>
          {current === 'sign-in' ? (
            <SignInPath token={token} onAccepted={setAccepted} />
          ) : info.requiresSignIn ? (
            <Alert severity="info" data-testid="invitation-has-account">
              {t('iam:invitation.has_account_notice')}
            </Alert>
          ) : (
            <NewAccountPath token={token} info={info} onAccepted={setAccepted} />
          )}
        </Box>
      </Stack>
    );
  }

  return (
    <PublicLayout
      screenId={SCR_AUTH_002}
      titleKey={owner ? 'iam:invitation.owner_title' : 'iam:invitation.title'}
      subtitle={
        info && !accepted
          ? t(owner ? 'iam:invitation.owner_subtitle' : 'iam:invitation.subtitle', {
              name: info.firstName ?? '',
              tenant: info.tenantName ?? t('shell:tenant.unnamed'),
            })
          : undefined
      }
    >
      {body}
    </PublicLayout>
  );
}
